import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";

import { ViolationMaintenancePanel } from "../../components/network/ViolationMaintenancePanel";
import { Alert } from "../../components/ui/Alert";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { CopyButton } from "../../components/ui/CopyButton";
import { Input } from "../../components/ui/Input";
import { PageHeader } from "../../components/ui/PageHeader";
import { ErrorState, LoadingState } from "../../components/ui/State";
import { Switch } from "../../components/ui/Switch";
import { ApiError } from "../../lib/api";
import {
  type CompanySettingsPatch,
  companiesApi,
} from "../../lib/api/companies";
import { formatMessage, t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

export function SettingsPage() {
  useLocale();
  const qc = useQueryClient();

  const [canonicalApiBaseUrl, setCanonicalApiBaseUrl] = useState<string>("");

  const [presenceAutoExpireSeconds, setPresenceAutoExpireSeconds] = useState<
    number | ""
  >("");
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [confirmRotateOpen, setConfirmRotateOpen] = useState(false);

  const [heartbeatInterval, setHeartbeatInterval] = useState(30);

  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState<number | "">(587);
  const [smtpUsername, setSmtpUsername] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpUseTls, setSmtpUseTls] = useState(true);
  const [smtpFromEmail, setSmtpFromEmail] = useState("");
  const [testEmailRecipient, setTestEmailRecipient] = useState("");
  const [testEmailResult, setTestEmailResult] = useState<
    { tone: "success" | "danger"; message: string } | null
  >(null);

  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["company-settings"],
    queryFn: () => companiesApi.getSettings(),
  });

  useEffect(() => {
    if (!query.data) return;
    setCanonicalApiBaseUrl(query.data.canonical_api_base_url || "");
    setPresenceAutoExpireSeconds(query.data.presence_auto_expire_seconds ?? "");
    setHeartbeatInterval(query.data.heartbeat_interval_seconds ?? 30);
    setSmtpHost(query.data.smtp_host ?? "");
    setSmtpPort(query.data.smtp_port ?? 587);
    setSmtpUsername(query.data.smtp_username ?? "");
    setSmtpUseTls(query.data.smtp_use_tls ?? true);
    setSmtpFromEmail(query.data.smtp_from_email ?? "");
    // smtp_password is write-only and never echoed back — the field always starts blank.
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (patch: CompanySettingsPatch) =>
      companiesApi.updateSettings(patch),
    onSuccess: (data, patch) => {
      if (Object.prototype.hasOwnProperty.call(patch, "smtp_host")) setSmtpPassword("");
      setSavedMessage(t("settings.savedMessage"));
      if (data.internal_presence_token_revealed) {
        setRevealedToken(data.internal_presence_token_revealed);
      }
      void qc.invalidateQueries({ queryKey: ["company-settings"] });
    },
  });

  function patchAndSave(patch: CompanySettingsPatch) {
    setSavedMessage(null);
    mutation.mutate(patch);
  }

  function handleSubmitCanonical(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    patchAndSave({ canonical_api_base_url: canonicalApiBaseUrl.trim() });
  }
  function onSavePresenceExpire() {
    const value =
      presenceAutoExpireSeconds === "" ? null : Number(presenceAutoExpireSeconds);
    patchAndSave({ presence_auto_expire_seconds: value });
  }
  function onRotateInternalToken() {
    setConfirmRotateOpen(true);
  }
  function confirmRotateInternalToken() {
    const token =
      crypto.randomUUID().replace(/-/g, "") +
      crypto.randomUUID().replace(/-/g, "");
    patchAndSave({ internal_presence_token: token });
    setConfirmRotateOpen(false);
  }
  function onSaveHeartbeatInterval() {
    patchAndSave({
      heartbeat_interval_seconds: heartbeatInterval,
    });
  }
  const smtpDirty = !!query.data && (
    smtpHost.trim() !== (query.data.smtp_host ?? "") ||
    (smtpPort === "" ? 587 : smtpPort) !== (query.data.smtp_port ?? 587) ||
    smtpUsername.trim() !== (query.data.smtp_username ?? "") ||
    smtpUseTls !== (query.data.smtp_use_tls ?? true) ||
    smtpFromEmail.trim() !== (query.data.smtp_from_email ?? "") || smtpPassword.length > 0
  );

  function onSaveSmtp() {
    const port = smtpPort === "" ? 587 : smtpPort;
    if (!Number.isInteger(port) || port < 1 || port > 65535) {
      setTestEmailResult({ tone: "danger", message: t("operations.smtp.invalidPort") });
      return;
    }
    if (smtpHost.trim().toLowerCase() === "smtp.gmail.com" && (port !== 587 || !smtpUseTls || !smtpUsername.trim())) {
      setTestEmailResult({ tone: "danger", message: t("operations.smtp.gmailRequirements") });
      return;
    }
    const patch: CompanySettingsPatch = {
      smtp_host: smtpHost.trim(),
      smtp_port: smtpPort === "" ? 587 : smtpPort,
      smtp_username: smtpUsername.trim(),
      smtp_use_tls: smtpUseTls,
      smtp_from_email: smtpFromEmail.trim(),
    };
    // Only send smtp_password when the admin actually typed a new one — the
    // serializer treats a blank/omitted value identically (leaves the
    // stored secret untouched), but omitting is the documented contract and
    // avoids re-sending a value we can never read back to confirm.
    if (smtpPassword.trim() !== "") {
      patch.smtp_password = smtpPassword;
    }
    setTestEmailResult(null);
    patchAndSave(patch);
  }

  const testEmailMutation = useMutation({
    mutationFn: (recipient: string) => companiesApi.testEmail(recipient),
    onSuccess: (data) => {
      if (!data.sent) {
        setTestEmailResult({ tone: "danger", message: t("operations.smtp.notSent") });
        return;
      }
      const sourceLabel = t(
        data.source === "company_smtp"
          ? "settings.smtp.testEmail.sourceCompanySmtp"
          : "settings.smtp.testEmail.sourceEnvFallback",
      );
      setTestEmailResult({
        tone: "success",
        message: formatMessage("settings.smtp.testEmail.success", {
          recipient: data.recipient,
          source: sourceLabel,
        }),
      });
    },
    onError: (err) => {
      setTestEmailResult({ tone: "danger", message: translateError(err) });
    },
  });

  function onSendTestEmail() {
    if (smtpDirty || mutation.isPending) {
      setTestEmailResult({ tone: "danger", message: t("operations.smtp.saveFirst") });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmailRecipient.trim())) {
      setTestEmailResult({
        tone: "danger",
        message: t("operations.smtp.validRecipient"),
      });
      return;
    }
    setTestEmailResult(null);
    testEmailMutation.mutate(testEmailRecipient.trim());
  }

  return (
    <div>
      <PageHeader
        eyebrow={t("settings.eyebrow")}
        title={t("settings.title")}
        subtitle={t("settings.subtitle")}
      />

      <Alert tone="info" title={t("settings.activationMovedTitle")}>
        {t("settings.activationMovedBody")}
      </Alert>

      {query.isLoading && <LoadingState />}
      {query.error && (
        <ErrorState
          titleKey="states.errorTitle"
          message={translateError(query.error)}
        />
      )}

      {query.data && (
        <div className="stack">
          {(savedMessage || mutation.error) && (
            <>
              {savedMessage && <Alert tone="success">{savedMessage}</Alert>}
              {mutation.error && (
                <Alert tone="danger">{translateError(mutation.error)}</Alert>
              )}
            </>
          )}

          <Card
            title={t("settings.canonical.title")}
            description={t("settings.canonical.description")}
          >
            <form onSubmit={handleSubmitCanonical}>
              <div className="field">
                <div className="field__label">
                  {t("settings.canonical_api_base_url.label")}
                  <span className="field__hint">
                    {t("settings.canonical_api_base_url.hint")}
                  </span>
                </div>
                <div className="field__control">
                  <Input
                    type="url"
                    value={canonicalApiBaseUrl}
                    onChange={(e) => setCanonicalApiBaseUrl(e.target.value)}
                    placeholder={t("settings.canonical_api_base_url.placeholder")}
                  />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  marginTop: 16,
                }}
              >
                <Button type="submit" loading={mutation.isPending}>
                  {t("common.save")}
                </Button>
              </div>
            </form>
          </Card>

          {query.data.presence_enforcement_feature_enabled && (
            <Card
              title={t("settings.presence.title")}
              description={t("settings.presence.description")}
            >
              <div style={{ marginBottom: 12 }}>
                <Alert tone="info">{t("settings.presence.info")}</Alert>
              </div>
              <div className="stack">
                <div className="field">
                  <div className="field__label">
                    {t("settings.presence.autoExpireLabel")}
                    <span className="field__hint">
                      {t("settings.presence.autoExpireHint")}
                    </span>
                  </div>
                  <div className="field__control" style={{ display: "flex", gap: 8 }}>
                    <Input
                      type="number"
                      min={60}
                      value={presenceAutoExpireSeconds}
                      onChange={(e) =>
                        setPresenceAutoExpireSeconds(
                          e.target.value === "" ? "" : Number(e.target.value),
                        )
                      }
                      placeholder="43200"
                    />
                    <Button
                      type="button"
                      onClick={onSavePresenceExpire}
                      loading={mutation.isPending}
                    >
                      {t("common.save")}
                    </Button>
                  </div>
                </div>

                <div className="field">
                  <div className="field__label">
                    {t("settings.presence.tokenLabel")}
                    <span className="field__hint">
                      {t("settings.presence.tokenHint")}
                    </span>
                  </div>
                  <div
                    className="field__control"
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <span>
                      {query.data.internal_presence_token_configured
                        ? t("settings.presence.tokenConfigured")
                        : t("settings.presence.tokenNotSet")}
                    </span>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onRotateInternalToken}
                    >
                      {t("settings.presence.tokenRotate")}
                    </Button>
                  </div>
                  {revealedToken && (
                    <div style={{ marginTop: 8 }}>
                      <Alert tone="warning">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <span>
                            {t("settings.presence.tokenRevealedPrefix")}{" "}
                            <code>{revealedToken}</code>
                          </span>
                          <CopyButton value={revealedToken} />
                        </div>
                      </Alert>
                    </div>
                  )}
                </div>

                <div className="field">
                  <div className="field__label">
                    {t("settings.presence.cidrLabel")}
                    <span className="field__hint">
                      {t("settings.presence.cidrHint")}
                    </span>
                  </div>
                  <div className="field__control">
                    <code>
                      {(query.data.internal_api_allowed_cidrs ?? []).join(", ")}
                    </code>
                  </div>
                </div>
              </div>
            </Card>
          )}

          <Card
            title={t("settings.reporting.title")}
            description={t("settings.reporting.description")}
          >
            <div style={{ marginBottom: 12 }}>
              <Alert tone="info">{t("settings.reporting.info")}</Alert>
            </div>
            <div className="stack">
              <div className="field">
                <div className="field__label">
                  {t("settings.reporting.heartbeatLabel")}
                  <span className="field__hint">
                    {t("settings.reporting.heartbeatHint")}
                  </span>
                </div>
                <div className="field__control">
                  <Input
                    type="number"
                    min={30}
                    value={heartbeatInterval}
                    onChange={(e) =>
                      setHeartbeatInterval(Number(e.target.value) || 30)
                    }
                  />
                </div>
              </div>
              <div
                style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}
              >
                <Button
                  type="button"
                  onClick={onSaveHeartbeatInterval}
                  loading={mutation.isPending}
                >
                  {t("common.save")}
                </Button>
              </div>
            </div>
          </Card>

          <Card
            title={t("settings.smtp.title")}
            description={t("settings.smtp.description")}
            actions={<Button type="button" variant="secondary" disabled={mutation.isPending} onClick={() => {
              setSmtpHost("smtp.gmail.com");
              setSmtpPort(587);
              setSmtpUseTls(true);
              setSmtpPassword("");
              setTestEmailResult(null);
            }}>{t("operations.smtp.gmailPreset")}</Button>}
          >
            <div className="stack">
              <Alert tone="info">
                <p>{t("operations.smtp.gmailHelp")}</p>
                <a href="https://support.google.com/mail/answer/185833" target="_blank" rel="noopener noreferrer">{t("operations.smtp.appPasswordHelp")}</a>
              </Alert>
              <p className="muted">{t(query.data.smtp_configured ? "operations.smtp.configured" : "operations.smtp.fallback")}</p>
              <div className="field">
                <div className="field__label">{t("settings.smtp.hostLabel")}</div>
                <div className="field__control">
                  <Input
                    value={smtpHost}
                    onChange={(e) => setSmtpHost(e.target.value)}
                    placeholder={t("settings.smtp.hostPlaceholder")}
                  />
                </div>
              </div>
              <div className="field">
                <div className="field__label">
                  {t("settings.smtp.portLabel")}
                  <span className="field__hint">{t("settings.smtp.portHint")}</span>
                </div>
                <div className="field__control">
                  <Input
                    type="number"
                    min={1}
                    max={65535}
                    value={smtpPort}
                    onChange={(e) =>
                      setSmtpPort(e.target.value === "" ? "" : Number(e.target.value))
                    }
                  />
                </div>
              </div>
              <div className="field">
                <div className="field__label">{t("settings.smtp.usernameLabel")}</div>
                <div className="field__control">
                  <Input
                    value={smtpUsername}
                    onChange={(e) => setSmtpUsername(e.target.value)}
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="field">
                <div className="field__label">
                  {t("settings.smtp.passwordLabel")}
                  <span className="field__hint">{t("settings.smtp.password.hint")}</span>
                </div>
                <div className="field__control">
                  <Input
                    type="password"
                    value={smtpPassword}
                    onChange={(e) => setSmtpPassword(e.target.value)}
                    placeholder={
                      query.data.smtp_configured
                        ? t("settings.smtp.password.placeholderConfigured")
                        : t("settings.smtp.password.placeholderEmpty")
                    }
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <div className="field">
                <div className="field__label">{t("settings.smtp.useTlsLabel")}</div>
                <div className="field__control">
                  <Switch checked={smtpUseTls} onChange={setSmtpUseTls} />
                </div>
              </div>
              <div className="field">
                <div className="field__label">
                  {t("settings.smtp.fromEmailLabel")}
                  <span className="field__hint">{t("settings.smtp.fromEmailHint")}</span>
                </div>
                <div className="field__control">
                  <Input
                    type="email"
                    value={smtpFromEmail}
                    onChange={(e) => setSmtpFromEmail(e.target.value)}
                  />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button type="button" onClick={onSaveSmtp} loading={mutation.isPending}>
                  {t("common.save")}
                </Button>
              </div>

              <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: 16 }}>
                <div className="field__label">{t("settings.smtp.testEmail.title")}</div>
                <p
                  className="field__hint"
                  style={{ marginTop: 4, marginBottom: 12 }}
                >
                  {t("settings.smtp.testEmail.description")}
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Input
                    type="email"
                    value={testEmailRecipient}
                    onChange={(e) => setTestEmailRecipient(e.target.value)}
                    placeholder={t("settings.smtp.testEmail.recipientPlaceholder")}
                    style={{ flex: "1 1 240px" }}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={onSendTestEmail}
                    loading={testEmailMutation.isPending}
                    disabled={smtpDirty || mutation.isPending}
                  >
                    {t("settings.smtp.testEmail.button")}
                  </Button>
                </div>
                {smtpDirty && <Alert tone="info">{t("operations.smtp.saveFirst")}</Alert>}
                {testEmailResult && (
                  <div style={{ marginTop: 8 }}>
                    <Alert tone={testEmailResult.tone}>{testEmailResult.message}</Alert>
                  </div>
                )}
              </div>
            </div>
          </Card>
          <ViolationMaintenancePanel />
        </div>
      )}

      <ConfirmDialog
        open={confirmRotateOpen}
        title={t("settings.presence.tokenRotateConfirmTitle")}
        body={t("settings.presence.tokenRotateConfirmBody")}
        confirmLabel={t("settings.presence.tokenRotate")}
        tone="primary"
        loading={mutation.isPending}
        onCancel={() => setConfirmRotateOpen(false)}
        onConfirm={confirmRotateInternalToken}
      />
    </div>
  );
}
